
import { Component, ChangeDetectionStrategy, input, output, effect, inject, signal, computed, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Customer } from '../../models/index';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CustomerService } from '../../services/customer.service';
import { NotificationService } from '../../services/notification.service';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { InputMaskDirective } from '../../directives/input-mask.directive';

// Custom Validator for Age
export function ageValidator(minAge: number): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; 
    }
    const birthDate = new Date(control.value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= minAge ? null : { 'underage': { requiredAge: minAge, actualAge: age } };
  };
}

@Component({
  selector: 'app-customer-form-modal',
  standalone: true,
  templateUrl: './customer-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, CommonModule, InputMaskDirective],
})
export class CustomerFormModalComponent implements AfterViewInit {
  customer = input<Customer | null>();
  isOpen = input.required<boolean>();
  
  closeModal = output<void>();
  saveCustomer = output<Omit<Customer, 'id' | 'createdAt' | 'hn'> | Customer>();

  customerForm: FormGroup;
  private customerService = inject(CustomerService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService); // Inject AuthService for settings

  // File Inputs
  @ViewChild('profileInput') profileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('idCardInput') idCardInput!: ElementRef<HTMLInputElement>;
  
  // Signature Pad
  @ViewChild('signaturePad') signaturePadElement!: ElementRef<HTMLCanvasElement>;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private isDrawing = false;

  // Upload States
  isUploadingProfile = signal(false);
  isUploadingIdCard = signal(false);
  
  // Computed state: Is any background process running?
  isProcessing = computed(() => this.isUploadingProfile() || this.isUploadingIdCard());
  
  // Previews
  profilePreview = signal<string | null>(null);
  idCardPreview = signal<string | null>(null);
  
  // Pending File State (for ID Card)
  pendingIdCardFile = signal<File | null>(null);
  pendingIdCardPreview = signal<string | null>(null);

  // Restricted Access for Dispensaries
  // Logic: Dispensaries cannot edit Name, ID, DOB, Medical History. Only Phone & Address.
  // UPDATED: Strict lock for Dispensary type, even for Owners/Admins (except Platform Admin).
  isRestrictedEdit = computed(() => {
     // If creating new customer (no ID), allow editing everything
     if (!this.customer()?.id) return false;
     
     // Platform Admin is god mode - can always edit
     const role = this.authService.currentUser()?.role;
     if (role === 'platform_admin') return false;

     // If Dispensary business type -> Restricted for EVERYONE (Owner, Admin, Doctor, Staff)
     // This ensures data consistency with the Clinic (Source of Truth).
     const businessType = this.authService.currentStore()?.businessType;
     if (businessType === 'DISPENSARY') {
         return true;
     }

     // For Clinics, standard behavior (allowed)
     return false;
  });

  constructor() {
    const fb: FormBuilder = inject(FormBuilder);
    this.customerForm = fb.group({
      id: [null],
      hn: [{ value: null, disabled: true }],
      fullName: ['', Validators.required],
      citizenId: [''],
      birthDate: ['', [Validators.required, ageValidator(20)]],
      phone: [''],
      address: [''], 
      idCardUrl: [''], 
      profile_image_url: [''],
      
      // Separate Medical Fields
      medicalHistory: [''],
      allergies: [''],
      underlyingDisease: [''],
      bloodType: [''],

      // Vital Signs
      temperature_c: [null],
      pulse_rate: [null],
      respiration_rate: [null],
      blood_pressure: [''],
      weight_kg: [null],
      height_cm: [null],

      card_uid: [''],
    });

    effect(() => {
      const customerData = this.customer();
      if (customerData) {
        this.customerForm.patchValue(customerData);
        // Set Previews (add timestamp to bust cache)
        const ts = Date.now();
        this.idCardPreview.set(customerData.idCardUrl ? `${customerData.idCardUrl}?t=${ts}` : null);
        this.profilePreview.set(customerData.profile_image_url ? `${customerData.profile_image_url}?t=${ts}` : null);
      } else {
        this.customerForm.reset({
          id: null,
          hn: null,
          fullName: '',
          citizenId: '',
          birthDate: '',
          phone: '',
          address: '',
          idCardUrl: '',
          profile_image_url: '',
          medicalHistory: '',
          allergies: '',
          underlyingDisease: '',
          bloodType: '',
          temperature_c: null,
          pulse_rate: null,
          respiration_rate: null,
          blood_pressure: '',
          weight_kg: null,
          height_cm: null,
          card_uid: '',
        });
        this.idCardPreview.set(null);
        this.profilePreview.set(null);
      }
      // Reset pending state on open/change
      this.cancelIdCardUpload();
    });
  }
  
  ngAfterViewInit() {
      // Re-initialize canvas if needed when view updates
      // This might be called multiple times, ensure idempotent
  }

  // --- Signature Pad Logic ---
  
  private initCanvas() {
    if (!this.signaturePadElement) return;
    
    const canvas = this.signaturePadElement.nativeElement;
    this.canvasContext = canvas.getContext('2d');
    
    if (this.canvasContext) {
      this.canvasContext.lineWidth = 2;
      this.canvasContext.lineCap = 'round';
      this.canvasContext.strokeStyle = 'blue'; // Blue pen for official look
    }

    // Event Listeners
    canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    canvas.addEventListener('mousemove', (e) => this.draw(e));
    canvas.addEventListener('mouseup', () => this.stopDrawing());
    canvas.addEventListener('mouseout', () => this.stopDrawing());
    
    // Touch support
    canvas.addEventListener('touchstart', (e) => this.startDrawing(e), { passive: false });
    canvas.addEventListener('touchmove', (e) => this.draw(e), { passive: false });
    canvas.addEventListener('touchend', () => this.stopDrawing());
  }
  
  private startDrawing(event: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    const pos = this.getEventPos(event);
    this.canvasContext?.beginPath();
    this.canvasContext?.moveTo(pos.x, pos.y);
    event.preventDefault(); 
  }

  private draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing) return;
    const pos = this.getEventPos(event);
    this.canvasContext?.lineTo(pos.x, pos.y);
    this.canvasContext?.stroke();
    event.preventDefault();
  }

  private stopDrawing() {
    this.isDrawing = false;
    this.canvasContext?.closePath();
  }
  
  private getEventPos(event: MouseEvent | TouchEvent) {
    const canvas = this.signaturePadElement.nativeElement;
    const rect = canvas.getBoundingClientRect();
    // Scale for canvas resolution vs display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = (event as MouseEvent).clientX;
      clientY = (event as MouseEvent).clientY;
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }
  
  clearSignature() {
    if(!this.signaturePadElement) return;
    const canvas = this.signaturePadElement.nativeElement;
    this.canvasContext?.clearRect(0, 0, canvas.width, canvas.height);
  }

  // --- End Signature Pad Logic ---

  onClose(): void {
    if (this.isProcessing()) {
        if(!confirm('กำลังอัปโหลดไฟล์ การปิดหน้าต่างอาจทำให้การอัปโหลดล้มเหลว ยืนยันหรือไม่?')) {
            return;
        }
    }
    this.closeModal.emit();
  }
  
  handleImageError(event: any, type: 'profile' | 'idcard') {
    // Prevent infinite loop if placeholder fails
    event.target.onerror = null;
    if (type === 'profile') {
        this.profilePreview.set(null); 
    } else {
        this.idCardPreview.set(null); 
    }
  }

  // --- Image Resizing Logic (Basic) ---
  private resizeImage(file: File, maxWidth: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height *= maxWidth / width;
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) { reject('Canvas error'); return; }
                
                ctx.drawImage(img, 0, 0, width, height);
                // Compress to JPEG 80% quality
                resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.src = e.target?.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
  }
  
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
  }

  // --- Profile Photo Upload ---
  async onProfilePhotoSelected(event: Event): Promise<void> {
    const file = this.getFileFromEvent(event);
    if (!file) return;

    const currentCustomer = this.customer();
    if (!currentCustomer?.id) {
        this.notificationService.showInfo('กรุณาบันทึกข้อมูลผู้ป่วยครั้งแรกก่อน จึงจะสามารถอัปโหลดรูปได้');
        this.resetFileInput(this.profileInput);
        return;
    }

    this.isUploadingProfile.set(true);
    try {
        // Resize to 800px width
        const base64 = await this.resizeImage(file, 800);
        const publicUrl = await this.customerService.uploadCustomerPhoto(currentCustomer.id, base64);
        
        this.customerForm.patchValue({ profile_image_url: publicUrl });
        this.notificationService.showSuccess('อัปโหลดรูปโปรไฟล์สำเร็จ');
        this.profilePreview.set(publicUrl); 
        
    } catch (e: any) {
        this.notificationService.showError(`อัปโหลดรูปโปรไฟล์ล้มเหลว: ${e.message}`);
    } finally {
        this.isUploadingProfile.set(false);
        this.resetFileInput(this.profileInput);
    }
  }

  // --- ID Card Upload Logic (UPDATED) ---
  
  onIdCardSelected(event: Event): void {
      const file = this.getFileFromEvent(event);
      if (!file) return;
      
      const currentCustomer = this.customer();
      if (!currentCustomer?.id) {
          this.notificationService.showInfo('กรุณาบันทึกข้อมูลผู้ป่วยครั้งแรกก่อน จึงจะสามารถอัปโหลดรูปได้');
          this.resetFileInput(this.idCardInput);
          return;
      }
      
      // Store file and show preview + signature pad
      this.pendingIdCardFile.set(file);
      
      // Create local preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
          this.pendingIdCardPreview.set(e.target?.result as string);
          // Wait for DOM update then init canvas
          setTimeout(() => this.initCanvas(), 100);
      };
      reader.readAsDataURL(file);
  }
  
  cancelIdCardUpload() {
      this.pendingIdCardFile.set(null);
      this.pendingIdCardPreview.set(null);
      this.resetFileInput(this.idCardInput);
  }
  
  async confirmIdCardUpload() {
      const file = this.pendingIdCardFile();
      const currentCustomer = this.customer();
      
      if(!file || !currentCustomer?.id) return;
      
      this.isUploadingIdCard.set(true);
      
      try {
          let signatureBase64: string | null = null;
          if (this.signaturePadElement) {
              const canvas = this.signaturePadElement.nativeElement;
              const blank = document.createElement('canvas');
              blank.width = canvas.width;
              blank.height = canvas.height;
              if (canvas.toDataURL() !== blank.toDataURL()) {
                  signatureBase64 = canvas.toDataURL('image/png');
              }
          }
          
          const storeSettings = this.authService.currentStore()?.clinic_settings;
          const watermarkText = storeSettings?.watermarkText || 'ใช้สำหรับขอใบสั่งจ่ายสมุนไพรควบคุม (กัญชา) เท่านั้น';

          const imageAsBase64 = await this.fileToBase64(file);
          const processedImageBase64 = await this.customerService.processIdCardImage(
              imageAsBase64, 
              signatureBase64, 
              currentCustomer.fullName || 'Unknown',
              watermarkText
          );
          
          const publicUrl = await this.customerService.uploadIdCardPhoto(currentCustomer.id, processedImageBase64);
          
          this.customerForm.patchValue({ idCardUrl: publicUrl });
          this.idCardPreview.set(publicUrl);
          
          this.notificationService.showSuccess('อัปโหลดสำเนาบัตรและลายเซ็นเรียบร้อยแล้ว');
          
          this.cancelIdCardUpload();

      } catch(e: any) {
          this.notificationService.showError(`อัปโหลดล้มเหลว: ${e.message}`);
      } finally {
          this.isUploadingIdCard.set(false);
      }
  }

  private getFileFromEvent(event: Event): File | null {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return null;
    const file = input.files[0];
    if (file.size > 10 * 1024 * 1024) { 
        this.notificationService.showError('ไฟล์ต้นฉบับใหญ่เกินไป (Max 10MB)');
        return null;
    }
    return file;
  }

  private resetFileInput(element: ElementRef<HTMLInputElement>) {
      if (element && element.nativeElement) {
          element.nativeElement.value = '';
      }
  }

  onSave(): void {
    if (this.customerForm.valid) {
      const rawData = this.customerForm.getRawValue();

      if (this.customer()) { // Editing mode
        this.saveCustomer.emit(rawData as Customer);
      } else { // Adding mode
        const { id, createdAt, hn, ...newCustomerData } = rawData;
        const payload = { 
          ...newCustomerData, 
          pointsBalance: 0, 
          tierLevel: 'Standard' 
        };
        this.saveCustomer.emit(payload);
      }
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isProcessing()) {
      this.onClose();
    }
  }
}
